import { createClient, type Factor, type SupabaseClient } from "@supabase/supabase-js";
import { validateBrowserEnvironment, type BrowserEnvironmentInput, type ValidatedBrowserEnvironment } from "../environment-safety";
import type { Database } from "../persistence/database.generated";

export const HOSTED_STAGING_AUTH_CALLBACK_PATH = "/auth/callback";
export const HOSTED_STAGING_AUTH_REHEARSAL_PATH = "/auth/rehearsal";
export const HOSTED_STAGING_AUTH_ORIGIN = "http://localhost:5173";

export interface HostedStagingAuthConfiguration {
  environment: ValidatedBrowserEnvironment;
  storageKey?: string;
}

export type AuthCallbackResult =
  | { status: "success" }
  | { status: "invalid"; message: string }
  | { status: "failed"; message: string };

export type HostedStagingSessionInvalidationResult =
  | { status: "invalidated" | "already_signed_out" }
  | { status: "failed"; message: string };

// React StrictMode deliberately replays mount effects in development. A PKCE
// authorisation code is single-use, so both replays must share one exchange.
// This retains only the in-flight/result promise for the current page module;
// a subsequent magic-link navigation loads a new page module.
let callbackExchangeFlight: Promise<AuthCallbackResult> | null = null;

export interface SanitisedHostedAuthDiagnostic {
  name?: string;
  status?: number;
  code?: string;
  message: string;
}

const sensitiveAuthText = /(?:https?:\/\/\S+|\b(?:access|refresh)[_-]?token\b\s*(?:=|:)\s*\S+|\b(?:api[_-]?key|authorization|secret)\b\s*(?:=|:)\s*\S+|\bsb_(?:secret|publishable)_[A-Za-z0-9_-]+)/giu;

/** Keeps provider diagnostics useful without surfacing credentials, links, or session material. */
export function sanitiseHostedAuthDiagnostic(error: unknown): SanitisedHostedAuthDiagnostic {
  const source = error && typeof error === "object" ? error as Record<string, unknown> : {};
  const name = typeof source.name === "string" && source.name.trim() ? source.name.trim().slice(0, 80) : undefined;
  const status = typeof source.status === "number" && Number.isInteger(source.status) ? source.status : undefined;
  const code = typeof source.code === "string" && /^[A-Za-z0-9_.-]{1,100}$/u.test(source.code) ? source.code : undefined;
  const rawMessage = typeof source.message === "string" ? source.message : "The authentication provider did not return a safe diagnostic.";
  const message = rawMessage.replace(sensitiveAuthText, "[redacted]").replace(/\s+/gu, " ").trim().slice(0, 240) || "The authentication provider did not return a safe diagnostic.";
  return { name, status, code, message };
}

export function formatHostedAuthDiagnostic(error: unknown): string {
  const diagnostic = sanitiseHostedAuthDiagnostic(error);
  const fields = [diagnostic.name && `type ${diagnostic.name}`, diagnostic.status !== undefined && `HTTP ${diagnostic.status}`, diagnostic.code && `code ${diagnostic.code}`, diagnostic.message].filter(Boolean);
  return fields.join("; ");
}

export function hostedStagingAuthCallbackUrl(origin = window.location.origin, mode = hostedStagingAuthConfiguration?.environment.mode): string {
  const url = new URL(origin);
  const production = mode === "hosted-production";
  if (origin !== HOSTED_STAGING_AUTH_ORIGIN && !(production && url.protocol === "https:" && url.hostname !== "localhost" && url.hostname !== "127.0.0.1")) throw new Error("Hosted authentication callback origin is not authorised.");
  return `${origin}${HOSTED_STAGING_AUTH_CALLBACK_PATH}`;
}

export function hostedStagingAuthEnvironment(input: BrowserEnvironmentInput): HostedStagingAuthConfiguration | null {
  const environment = validateBrowserEnvironment(input);
  const developmentAcceptance = import.meta.env.DEV && environment.mode === "hosted-acceptance";
  if ((environment.mode !== "hosted-staging" && environment.mode !== "hosted-production" && !developmentAcceptance) || !environment.supabase) return null;
  return { environment, storageKey: `mo-${environment.mode}-auth-${environment.supabase.projectRef}` };
}

export function createHostedStagingAuthClient(configuration: HostedStagingAuthConfiguration): SupabaseClient<Database> {
  const details = configuration.environment.supabase;
  const developmentAcceptance = import.meta.env.DEV && configuration.environment.mode === "hosted-acceptance";
  if ((configuration.environment.mode !== "hosted-staging" && configuration.environment.mode !== "hosted-production" && !developmentAcceptance) || !details) {
    throw new Error("Hosted staging authentication is not configured.");
  }
  return createClient<Database>(details.url, details.anonKey, { auth: hostedStagingAuthOptions(configuration.storageKey) });
}

export function hostedStagingAuthOptions(storageKey?: string) {
  return { storageKey, persistSession: true, autoRefreshToken: true, detectSessionInUrl: false, flowType: "pkce" as const };
}

/** A later AAL1 session challenges the factor that was already verified; it never re-enrols it. */
export function verifiedTotpFactor(factors: readonly Factor[]): Factor | null {
  return factors.find((factor) => factor.factor_type === "totp" && factor.status === "verified") ?? null;
}

function browserInput(): BrowserEnvironmentInput {
  return {
    target: import.meta.env.VITE_PERSISTENCE_TARGET as string | undefined,
    dataMode: import.meta.env.VITE_DATA_MODE as string | undefined,
    supabaseUrl: import.meta.env.VITE_SUPABASE_URL as string | undefined,
    supabaseAnonKey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string | undefined,
    expectedProjectRef: import.meta.env.VITE_SUPABASE_EXPECTED_PROJECT_REF as string | undefined,
    expectedProjectName: import.meta.env.VITE_SUPABASE_EXPECTED_PROJECT_NAME as string | undefined,
    expectedRegion: import.meta.env.VITE_SUPABASE_EXPECTED_REGION as string | undefined,
    variables: import.meta.env,
  };
}

const browserTarget = (import.meta.env.VITE_PERSISTENCE_TARGET as string | undefined)?.trim();
export const hostedProductionRequested = browserTarget === "hosted-production";

function initialiseHostedStagingAuth(): { configuration: HostedStagingAuthConfiguration | null; error: string | null } {
  try {
    return { configuration: hostedStagingAuthEnvironment(browserInput()), error: null };
  } catch (error) {
    return { configuration: null, error: error instanceof Error ? error.message : "Hosted browser configuration is invalid." };
  }
}

const hostedStagingAuthInitialisation = initialiseHostedStagingAuth();
export const hostedStagingAuthConfiguration = hostedStagingAuthInitialisation.configuration;
export const hostedStagingAuthConfigurationError = hostedStagingAuthInitialisation.error;
export const hostedStagingAuthClient = hostedStagingAuthConfiguration
  ? createHostedStagingAuthClient(hostedStagingAuthConfiguration)
  : null;

export function isHostedStagingAuthRehearsal(): boolean {
  return hostedStagingAuthConfiguration?.environment.mode === "hosted-staging" || (import.meta.env.DEV && hostedStagingAuthConfiguration?.environment.mode === "hosted-acceptance");
}
export function isHostedProductionEnvironment(): boolean { return hostedStagingAuthConfiguration?.environment.mode === "hosted-production"; }

/** Acceptance is deliberately blocked from opening the Local workspace until A2D-4A adds its test-only harness. */
export function isDisposableHostedAcceptanceEnvironment(): boolean {
  return import.meta.env.DEV && validateBrowserEnvironment(browserInput()).mode === "hosted-acceptance";
}

export function isHostedStagingAuthRoute(pathname = window.location.pathname): boolean {
  return pathname === HOSTED_STAGING_AUTH_CALLBACK_PATH || pathname === HOSTED_STAGING_AUTH_REHEARSAL_PATH;
}

export async function exchangeHostedStagingAuthCallback(
  client: Pick<SupabaseClient, "auth">,
  search = window.location.search,
): Promise<AuthCallbackResult> {
  const parameters = new URLSearchParams(search);
  const suppliedError = parameters.get("error_description") || parameters.get("error");
  if (suppliedError) return { status: "failed", message: "The hosted staging sign-in link was not accepted." };
  const code = parameters.get("code");
  if (!code || Array.from(parameters.keys()).length !== 1) return { status: "invalid", message: "The hosted staging callback is incomplete." };
  const { error } = await client.auth.exchangeCodeForSession(code);
  return error ? { status: "failed", message: "The hosted staging session could not be established." } : { status: "success" };
}

/** Shares the one permitted PKCE code exchange across duplicate development effects. */
export function exchangeHostedStagingAuthCallbackOnce(
  client: Pick<SupabaseClient, "auth">,
  search = window.location.search,
): Promise<AuthCallbackResult> {
  callbackExchangeFlight ??= exchangeHostedStagingAuthCallback(client, search);
  return callbackExchangeFlight;
}

export async function requestHostedStagingMagicLink(client: Pick<SupabaseClient, "auth">, email: string) {
  return client.auth.signInWithOtp({
    email,
    options: { shouldCreateUser: false, emailRedirectTo: hostedStagingAuthCallbackUrl() },
  });
}

export async function invalidateHostedStagingSession(
  client: Pick<SupabaseClient, "auth">,
  storageKey: string,
  storage: Pick<Storage, "removeItem"> = window.localStorage,
): Promise<HostedStagingSessionInvalidationResult> {
  const before = await client.auth.getSession();
  if (before.error) return { status: "failed", message: "The hosted staging session could not be checked." };
  if (before.data.session) {
    const signedOut = await client.auth.signOut({ scope: "global" });
    if (signedOut.error) return { status: "failed", message: "The hosted staging session could not be invalidated." };
  }
  storage.removeItem(storageKey);
  const after = await client.auth.getSession();
  if (after.error || after.data.session) return { status: "failed", message: "The hosted staging session could not be confirmed as signed out." };
  return { status: before.data.session ? "invalidated" : "already_signed_out" };
}

/** An expired session cannot establish a hosted identity; clear only its isolated Auth namespace. */
export function discardExpiredHostedStagingSession(
  storageKey: string,
  storage: Pick<Storage, "removeItem"> = window.localStorage,
): void {
  storage.removeItem(storageKey);
}
